import { ENDPOINT } from '~/constants/endpoints';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type CreateUserPayload, type CreateUserResponse } from './types';
import { zodCreateUserSchema } from './validations';

/**
 * Creates a new user as ADMIN.
 *
 * @param {Object} userData - The user data for creating a new user.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/create_user_vadmin_users_post
 */
export const createUserAdmin = async (userData: CreateUserPayload) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.USER.CREATE_ADMIN(), {
        json: userData,
        validate: { body: zodCreateUserSchema },
      })
      .json<CreateUserResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error creating user',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.user.createUserAdmin.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.user.createUserAdmin.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
